import { FC, useMemo } from "react";
import { List, ListItemButton, Stack, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconLookup, IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";

type Props = {
	mode?: 'desktop' | 'mobile';
	animationDelay?: number;
};

type SocialLink = {
	label: string;
	icon: [IconPrefix, IconName] | IconLookup;
	link: string;
	index?: number;
};

const SocialNav: FC<Props> = ({ mode = 'desktop', animationDelay = 0}) => {
	const theme = useTheme();
	const socialLinks = useMemo(() => [
		{
			label: 'Spotify',
			icon: ['fab', 'spotify'] as [IconPrefix, IconName],
			link: 'https://open.spotify.com/artist/3VkqhXNYjLfBbSHE0vQzp7?si=oJ8-jFD1RUW-SylvDLlVcQ&dl_branch=1'
		},
		{
			label: 'YouTube',
			icon: ['fab', 'youtube'] as [IconPrefix, IconName],
			link: 'https://www.youtube.com/channel/UC_47vjBpUZwBdn4Rk8Ptt1Q'
		},
		{
			label: 'Facebook',
			icon: ['fab', 'facebook'] as [IconPrefix, IconName],
			link: 'https://www.facebook.com/Gravitas-Music-LLC-325092328343266'
		},
		{
			label: 'SoundCloud',
			icon: ['fab', 'soundcloud'] as [IconPrefix, IconName],
			link: 'https://soundcloud.com/timkuligfreemusic'
		},
		{
			label: 'Patreon',
			icon: ['fab', 'patreon'] as [IconPrefix, IconName],
			link: 'https://www.patreon.com/user?u=34013291&fan_landing=true'
		},
		{
			label: 'Email',
			icon: ['fas', 'envelope'] as [IconPrefix, IconName],
			link: 'mailto:timkuligfreemusic@gmail.com'
		}
	], []);

	const SocialLinkButton: FC<SocialLink> = useMemo(() => ({ label, icon, link, index }) => {
		return (
			<ListItemButton
				href={link}
				target="_blank"
				className="animated fadeInUp"
				sx={{
					width: mode === 'mobile' ? '100%' : 'auto',
					mb: 2,
					animationDelay: `${animationDelay + 0.1 + (index || 0) * 0.05}s`
				}}
			>
				<Stack direction="row" spacing={1} justifyContent={mode === 'mobile' ? 'center' : 'flex-start'} alignItems="center" width="100%">
					<FontAwesomeIcon icon={icon} style={{ color: theme.palette.text.primary, opacity: 0.4 }} fixedWidth />
					<Typography variant="button" color="textPrimary" fontSize={12} sx={{ opacity: 0.8 }}>{label}</Typography>
					{mode === 'mobile' && (
						<FontAwesomeIcon icon={icon} style={{ color: theme.palette.text.primary, opacity: 0 }} fixedWidth />
					)}
				</Stack>
			</ListItemButton>
		);
	}, [animationDelay, mode, theme.palette.text.primary]);

	return (
		<Stack
			spacing={1}
			className={`${mode === 'mobile' ? 'animated fadeInUp' : ''}`}
			sx={{
				borderTopStyle: 'solid',
				borderTopColor: theme.palette.divider,
				borderTopWidth: 1,
				pt: 4,
				animationDelay: `${animationDelay}s`
			}}
		>
			<Typography
				variant="button"
				color="textPrimary"
				className="aniamted fadeInUp"
				sx={{
					pl: mode === 'mobile' ? 0 : 2,
					fontSize: '0.75rem',
					opacity: 0.6,
					textAlign: mode === 'mobile' ? 'center' : 'left',
					animationDelay: `${animationDelay + 0.05}s`
				}}
			>
				Find me on
			</Typography>
			<List>
				{socialLinks.map((link, index) => (
					<SocialLinkButton key={index} label={link.label} icon={link.icon} link={link.link} index={index} />
				))}
			</List>
		</Stack>
	);
};

export default SocialNav;